/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Pencil, Trash2, Plus } from 'lucide-react'
import { DataTable } from '@/components/CustomTable'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import Loading from '@/components/Loading'
import { toast } from 'sonner'
import type { ColumnDef } from '@tanstack/react-table'
import {
  useUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} from '@/hooks/useUsers'
import type { UserRow } from '@/types/backend'

export default function UsersPage() {
  const { data: users, status, isFetching } = useUsersQuery(true)
  const createMut = useCreateUserMutation()
  const deleteMut = useDeleteUserMutation()
  const [editing, setEditing] = useState<UserRow | null>(null)
  const updateMut = useUpdateUserMutation(editing?.id || '')

  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const [form, setForm] = useState<{
    name: string
    email: string
    password: string
    role: 'ADMIN' | 'STAFF'
    active: boolean
  }>({ name: '', email: '', password: '', role: 'STAFF', active: true })

  const columns = useMemo<ColumnDef<UserRow>[]>(
    () => [
      { accessorKey: 'name', header: 'Nom' },
      { accessorKey: 'email', header: 'Email' },
      { accessorKey: 'role', header: 'Rôle' },
      {
        accessorKey: 'active',
        header: 'Actif',
        cell: ({ row }) => (row.original.active ? 'Oui' : 'Non'),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className='flex items-center gap-3'>
            <Pencil
              size={18}
              className='cursor-pointer text-green-600 mr-4'
              onClick={() => {
                const u = row.original
                setEditing(u)
                setForm({
                  name: u.name,
                  email: u.email,
                  password: '',
                  role: u.role === 'ADMIN' ? 'ADMIN' : 'STAFF',
                  active: !!u.active,
                })
                setOpen(true)
              }}
            />
            <Trash2
              size={18}
              className='cursor-pointer text-red-600 ml-4'
              onClick={() => {
                setPendingDeleteId(row.original.id)
                setConfirmOpen(true)
              }}
            />
          </div>
        ),
      },
    ],
    []
  )

  const openCreate = () => {
    setEditing(null)
    setForm({ name: '', email: '', password: '', role: 'STAFF', active: true })
    setOpen(true)
  }

  const submit = async () => {
    const name = form.name.trim()
    const email = form.email.trim().toLowerCase()
    const password = form.password.trim()

    if (!name) return toast.error('Nom requis')
    if (!email) return toast.error('Email requis')
    if (!editing && password.length < 8) {
      return toast.error('Mot de passe requis (min 8) pour création')
    }

    try {
      if (editing) {
        await updateMut.mutateAsync({
          name: name || undefined,
          email: email || undefined,
          role: form.role,
          active: form.active,
          // password est optionnel en édition
          ...(password ? { password } : {}),
        })
        toast.success('Utilisateur modifié')
      } else {
        await createMut.mutateAsync({
          name,
          email,
          password,
          role: form.role,
        })
        toast.success('Utilisateur créé')
      }
      setOpen(false)
    } catch (e: any) {
      toast.error('Action échouée', { description: e?.response?.data?.message })
    }
  }

  const confirmDelete = async () => {
    if (!pendingDeleteId) return
    try {
      await deleteMut.mutateAsync(pendingDeleteId)
      toast.success('Utilisateur supprimé')
      setConfirmOpen(false)
      setPendingDeleteId(null)
    } catch (e: any) {
      toast.error('Suppression échouée', {
        description: e?.response?.data?.message,
      })
    }
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-2xl font-semibold'>Utilisateurs</h3>
        <div className='flex items-center gap-2'>
          <Button onClick={openCreate}>
            <Plus className='mr-2 h-4 w-4' />
            Ajouter
          </Button>
        </div>
      </div>

      {status === 'pending' ? (
        <Loading />
      ) : (
        <>
          <div className='text-sm text-muted-foreground'>
            Total: <b>{users?.length ?? 0}</b>
          </div>
          <DataTable<UserRow, unknown>
            columns={columns}
            data={users ?? []}
            total={users?.length ?? 0}
            pageCount={1}
            // Si tu veux la recherche serveur, plug un hook similaire à Items avec useServerTableControls
          />
          <div className='text-xs text-muted-foreground'>
            {isFetching ? 'Actualisation…' : null}
          </div>
        </>
      )}

      {/* Modal Créer / Éditer */}
      <AppDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Modifier un utilisateur' : 'Ajouter un utilisateur'}
        description={
          editing
            ? 'Mettez à jour les informations.'
            : 'Renseignez les champs requis.'
        }
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        footer={
          <ModalActions
            onConfirm={submit}
            confirmLabel='Valider'
            confirmLoading={createMut.isPending || updateMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <div className='grid gap-1.5'>
            <Label>Nom</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder='Ex: Jean Dupont'
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Email</Label>
            <Input
              value={form.email}
              onChange={(e) =>
                setForm((f) => ({ ...f, email: e.target.value }))
              }
              placeholder='Ex: user@demo.com'
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Mot de passe {editing ? '(optionnel)' : ''}</Label>
            <Input
              type='password'
              value={form.password}
              onChange={(e) =>
                setForm((f) => ({ ...f, password: e.target.value }))
              }
              placeholder={
                editing
                  ? 'Laisser vide pour ne pas changer'
                  : 'Min 8 caractères'
              }
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Rôle</Label>
            <Select
              value={form.role}
              onValueChange={(v: 'ADMIN' | 'STAFF') =>
                setForm((f) => ({ ...f, role: v }))
              }
            >
              <SelectTrigger className='w-[220px]'>
                <SelectValue placeholder='Choisir un rôle' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='ADMIN'>ADMIN</SelectItem>
                <SelectItem value='STAFF'>STAFF</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {editing && (
            <div className='flex items-center justify-between rounded-md border p-3'>
              <div className='space-y-0.5'>
                <Label>Actif</Label>
                <div className='text-xs text-muted-foreground'>
                  Désactive l’accès sans supprimer le compte.
                </div>
              </div>
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
              />
            </div>
          )}
        </div>
      </AppDialog>

      {/* Confirmation suppression */}
      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title='Supprimer l’utilisateur'
        description='Cette action est irréversible.'
        footer={
          <ModalActions
            confirmVariant='destructive'
            confirmLabel='Supprimer'
            confirmLoading={deleteMut.isPending}
            onConfirm={confirmDelete}
          />
        }
      >
        <div className='px-4 py-2 text-sm'>
          Voulez-vous vraiment supprimer cet utilisateur ?
        </div>
      </AppDialog>
    </div>
  )
}
